import React from 'react';
import KPICard from '../ui/KPICard';
import Card from '../ui/Card';
import Section from '../ui/Section';
import Table from '../ui/Table';
import { formatNumber, formatDuration } from '../dashboardUtils';
import {
    usePageViewsQuery,
    useSessionDurationQuery,
    useTopPagesQuery,
    useVisitorsOverviewQuery,
} from '../../../../api/page.api';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
} from 'recharts';

/**
 * Website Analytics View Component
 */
const WebsiteAnalyticsView = () => {
    const { data: pageViewsData, isLoading: pageViewsLoading } = usePageViewsQuery({});
    const { data: sessionDurationData, isLoading: sessionLoading } = useSessionDurationQuery({});
    const { data: topPagesData, isLoading: topPagesLoading } = useTopPagesQuery({});
    const { data: visitorsData, isLoading: visitorsLoading } = useVisitorsOverviewQuery({});

    const pageViews = pageViewsData?.result || [];
    const sessionDuration = sessionDurationData?.result?.[0] || {};
    const topPages = topPagesData?.result || [];
    const visitors = visitorsData?.result || {};

    const maxViews = Math.max(...topPages.map((p) => p.totalViews || 0), 1);

    // Format top 8 pages for Recharts Bar Chart
    const chartData = pageViews.slice(0, 8).map((p) => ({
        name: p._id === '/' ? 'Homepage' : p._id?.length > 15 ? `${p._id.slice(0, 15)}...` : p._id || 'Page',
        Views: p.totalViews || 0,
    }));

    const columns = [
        {
            key: 'rank',
            label: 'Rank',
            align: 'center',
            render: (_, __, index) => (
                <span
                    className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-xs ${
                        index < 3
                            ? 'bg-stone-900 text-white font-bold'
                            : 'bg-stone-100 text-stone-600 font-medium'
                    }`}
                >
                    {index + 1}
                </span>
            ),
        },
        {
            key: '_id',
            label: 'Page URL / Path',
            render: (path) => (
                <div>
                    <div className="font-semibold text-stone-900">{path || 'Unknown'}</div>
                    <div className="text-[11px] text-stone-400 font-normal">
                        {path === '/'
                            ? 'Main Homepage'
                            : path?.includes('/product')
                            ? 'Product Detail'
                            : path?.includes('/collection')
                            ? 'Collection Catalog'
                            : path?.includes('/check_out')
                            ? 'Checkout Funnel'
                            : 'General Page'}
                    </div>
                </div>
            ),
        },
        {
            key: 'totalViews',
            label: 'Total Views',
            align: 'right',
            render: (val) => (
                <span className="font-bold text-stone-900">{formatNumber(val)}</span>
            ),
        },
        {
            key: 'popularity',
            label: 'Traffic Share',
            align: 'center',
            render: (_, row) => {
                const percent = Math.min(((row.totalViews || 0) / maxViews) * 100, 100);
                return (
                    <div className="w-32 bg-stone-100 rounded-full h-2 overflow-hidden mx-auto">
                        <div
                            className="bg-stone-900 h-full rounded-full transition-all duration-300"
                            style={{ width: `${percent}%` }}
                        />
                    </div>
                );
            },
        },
    ];

    return (
        <div className="space-y-8 w-full min-w-0">
            {/* Visitors KPI Cards */}
            <Section title="Website Traffic Overview" subtitle="User engagements and site visitor counts">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 w-full min-w-0">
                    <KPICard
                        label="Total Visitors"
                        value={formatNumber(visitors.totalVisitors || 0)}
                        loading={visitorsLoading}
                        accentClass="bg-stone-900 text-white"
                        icon={
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                        }
                    />
                    <KPICard
                        label="New Visitors"
                        value={formatNumber(visitors.newVisitors || 0)}
                        loading={visitorsLoading}
                        accentClass="bg-emerald-600 text-white"
                        icon={
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                            </svg>
                        }
                    />
                    <KPICard
                        label="Returning Visitors"
                        value={formatNumber(visitors.returningVisitors || 0)}
                        loading={visitorsLoading}
                        accentClass="bg-amber-600 text-white"
                        icon={
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                        }
                    />
                    <KPICard
                        label="Avg Session Duration"
                        value={formatDuration(sessionDuration.avgDuration)}
                        loading={sessionLoading}
                        accentClass="bg-indigo-600 text-white"
                        icon={
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        }
                    />
                </div>
            </Section>

            {/* Session Duration Breakdown Cards */}
            <Section title="Session Duration Analytics" subtitle="Time spent per session statistics">
                <Card noPadding>
                    <div className="p-6 w-full min-w-0">
                        {sessionLoading ? (
                            <div className="py-6 text-center text-xs text-stone-400">Loading session data...</div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full min-w-0">
                                <div className="p-4 bg-stone-50 rounded-xl text-center">
                                    <p className="text-xl font-bold text-stone-900">
                                        {formatDuration(sessionDuration.avgDuration)}
                                    </p>
                                    <p className="text-xs text-stone-400 mt-1 uppercase font-semibold">
                                        Average Duration
                                    </p>
                                </div>
                                <div className="p-4 bg-emerald-50/50 rounded-xl text-center">
                                    <p className="text-xl font-bold text-emerald-700">
                                        {formatDuration(sessionDuration.maxDuration)}
                                    </p>
                                    <p className="text-xs text-emerald-600/80 mt-1 uppercase font-semibold">
                                        Longest Session
                                    </p>
                                </div>
                                <div className="p-4 bg-rose-50/50 rounded-xl text-center">
                                    <p className="text-xl font-bold text-rose-700">
                                        {formatDuration(sessionDuration.minDuration)}
                                    </p>
                                    <p className="text-xs text-rose-600/80 mt-1 uppercase font-semibold">
                                        Shortest Session
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </Card>
            </Section>

            {/* Page Views Chart & Most Visited Pages Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 w-full min-w-0">
                {/* Recharts Bar Chart */}
                <div className="lg:col-span-6 min-w-0">
                    <Section title="Page Views Distribution" subtitle="Top accessed URLs chart">
                        <Card noPadding>
                            <div className="p-6 w-full min-w-0">
                                {pageViewsLoading ? (
                                    <div className="h-72 flex items-center justify-center text-xs text-stone-400">
                                        Loading page views chart...
                                    </div>
                                ) : chartData.length === 0 ? (
                                    <div className="h-72 flex items-center justify-center text-xs text-stone-400">
                                        No page view data available
                                    </div>
                                ) : (
                                    <div className="h-80 w-full min-w-0">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart
                                                data={chartData}
                                                layout="vertical"
                                                margin={{ top: 10, right: 20, left: 20, bottom: 0 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                                <XAxis type="number" stroke="#94a3b8" fontSize={11} />
                                                <YAxis dataKey="name" type="category" stroke="#94a3b8" fontSize={11} width={90} />
                                                <Tooltip
                                                    contentStyle={{
                                                        backgroundColor: '#0f172a',
                                                        borderRadius: '0.75rem',
                                                        color: '#f8fafc',
                                                        fontSize: '12px',
                                                    }}
                                                    formatter={(val) => [formatNumber(val), 'Views']}
                                                />
                                                <Bar dataKey="Views" fill="#0f172a" radius={[0, 6, 6, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                )}
                            </div>
                        </Card>
                    </Section>
                </div>

                {/* Top Visited Pages Table */}
                <div className="lg:col-span-6 min-w-0">
                    <Section title="Most Visited Pages" subtitle="Ranked list by view count">
                        <Table
                            columns={columns}
                            data={topPages}
                            keyField="_id"
                            emptyMessage="No traffic recorded yet"
                        />
                    </Section>
                </div>
            </div>
        </div>
    );
};

export default WebsiteAnalyticsView;
